import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Droplet, Lock, Send, Users, MessageCircle, Heart, HandHelping, Sparkles, Pencil, X as XIcon, Camera, Loader2, Scissors, Play, Trash2 } from "lucide-react";
import { portalApi, apiError, mediaUrl } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import { BADGE_ICONS } from "./badgeIcons";
import MediaGrid from "./MediaGrid";
import MediaAttach from "./MediaAttach";
import VoiceRecorder from "./VoiceRecorder";
import VoicePlayer from "./VoicePlayer";
import LiveButton from "./LiveButton";
import Avatar from "./Avatar";
import { toast } from "sonner";

const TYPE_LABEL = { reflection: "Reflection", journal: "Journal", story: "Story Harvest", post: "Post" };
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function AboutCard({ about, isSelf, firstName }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ about: about.about || "", likes: about.likes || "", prayer: about.prayer || "", rapid: about.rapid || {} });
  const [saving, setSaving] = useState(false);
  const questions = about.questions || [];
  const answered = questions.filter((q) => form.rapid[q.id]);
  const hasContent = form.about || form.likes || form.prayer || answered.length > 0;

  const save = async () => {
    setSaving(true);
    try { await portalApi.put("/portal/profile/about", form); setEditing(false); toast.success("Saved."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <section className="mb-12" data-testid="about-edit">
        <div className="flex items-center justify-between mb-4"><p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Your blessing card</p><button onClick={() => setEditing(false)}><XIcon className="w-4 h-4 text-[#8a7a6a]" strokeWidth={1.5} /></button></div>
        <div className="bg-white/60 border border-[#E0D8C9] rounded-2xl p-6 space-y-5">
          {[["about", "About me", "Tell your cohort who you are…"], ["likes", "What I love / what fills me", "Long walks, my kids' laughter, worship music…"], ["prayer", "What I need prayer for", "Be honest — this is how your cohort will bless you…"]].map(([k, label, ph]) => (
            <div key={k}>
              <label className="text-sm font-body text-[#5E4B3C] block mb-1.5">{label}</label>
              <textarea data-testid={`about-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={k === "about" ? 3 : 2} placeholder={ph}
                className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] resize-none" />
            </div>
          ))}
          <div>
            <p className="text-sm font-body text-[#5E4B3C] mb-2">Rapid fire</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {questions.map((q) => (
                <div key={q.id}>
                  <label className="text-xs text-[#8a7a6a] font-body block mb-1">{q.q}</label>
                  <input data-testid={`rapid-${q.id}`} value={form.rapid[q.id] || ""} onChange={(e) => setForm({ ...form, rapid: { ...form.rapid, [q.id]: e.target.value } })}
                    className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-1.5 text-sm font-body outline-none focus:border-[#A9724E]" />
                </div>
              ))}
            </div>
          </div>
          <button data-testid="about-save" onClick={save} disabled={saving} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-sm disabled:opacity-50">{saving ? "Saving…" : "Save blessing card"}</button>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-12" data-testid="about-card">
      <div className="flex items-center justify-between mb-4">
        <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">{isSelf ? "Your blessing card" : `A little bit about ${firstName}`}</p>
        {isSelf && <button data-testid="about-edit-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-xs text-[#A9724E] hover:underline"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />{hasContent ? "Edit" : "Fill this in"}</button>}
      </div>
      {!hasContent ? (
        <div className="bg-white/40 border border-dashed border-[#D8CEBE] rounded-2xl p-6 text-center text-[#8a7a6a] font-body text-sm">
          {isSelf ? "Add a little about yourself so your cohort knows how to encourage you." : `${firstName} hasn't filled this in yet.`}
        </div>
      ) : (
        <div className="bg-white/60 border border-[#E0D8C9] rounded-2xl p-6 space-y-5">
          {form.about && <div><p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />About</p><p className="font-body text-[#2A2825] leading-relaxed whitespace-pre-wrap">{form.about}</p></div>}
          {form.likes && <div><p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1.5 flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" strokeWidth={1.5} />What fills me</p><p className="font-body text-[#2A2825] leading-relaxed whitespace-pre-wrap">{form.likes}</p></div>}
          {form.prayer && <div className="bg-[#EAE3D9]/60 -mx-2 px-4 py-3 rounded-lg"><p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1.5 flex items-center gap-1.5"><HandHelping className="w-3.5 h-3.5" strokeWidth={1.5} />Praying for</p><p className="font-body text-[#2A2825] leading-relaxed whitespace-pre-wrap">{form.prayer}</p></div>}
          {answered.length > 0 && (
            <div>
              <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-2">Rapid fire</p>
              <div className="grid grid-cols-2 gap-3">
                {answered.map((q) => (
                  <div key={q.id} className="border-l-2 border-[#D8CEBE] pl-3"><p className="text-[11px] text-[#8a7a6a] font-body">{q.q}</p><p className="font-serif-display text-[#2A2825]">{form.rapid[q.id]}</p></div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function NotifPrefs({ profile }) {
  const [phone, setPhone] = useState(profile.phone || "");
  const [sms, setSms] = useState(!!profile.sms_opt_in);
  const [email, setEmail] = useState(profile.email_opt_in !== false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await portalApi.put("/portal/notifications/prefs", { phone, sms_opt_in: sms, email_opt_in: email }); toast.success("Preferences saved."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const Toggle = ({ on, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`w-11 h-6 rounded-full transition-colors relative ${on ? "bg-[#A9724E]" : "bg-[#D8CEBE]"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
  return (
    <section className="mb-12" data-testid="notif-prefs">
      <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">Reminders · never miss a challenge or gathering</p>
      <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 space-y-5">
        <div>
          <label className="text-sm font-body text-[#5E4B3C] block mb-1.5">Mobile number (for text reminders)</label>
          <input data-testid="prefs-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567"
            className="w-full max-w-xs bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E]" />
        </div>
        <div className="flex items-center justify-between max-w-sm"><span className="font-body text-[#2A2825]">Text me reminders (SMS)</span><Toggle on={sms} onClick={() => setSms((v) => !v)} testid="prefs-sms-toggle" /></div>
        <div className="flex items-center justify-between max-w-sm"><span className="font-body text-[#2A2825]">Email me reminders</span><Toggle on={email} onClick={() => setEmail((v) => !v)} testid="prefs-email-toggle" /></div>
        <button data-testid="prefs-save" onClick={save} disabled={saving} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-sm transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save preferences"}</button>
      </div>
    </section>
  );
}

export default function Profile() {
  const { userId } = useParams();
  const { user, refreshUser } = usePortalAuth();
  const navigate = useNavigate();
  const targetId = userId || user?.id;
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dir, setDir] = useState([]);
  const [wall, setWall] = useState([]);
  const [wallText, setWallText] = useState("");
  const [wallMedia, setWallMedia] = useState([]);
  const [posting, setPosting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [recs, setRecs] = useState([]);
  const [badgeDetail, setBadgeDetail] = useState(null);
  const [editName, setEditName] = useState(null);
  const [savingName, setSavingName] = useState(false);
  const [pwModal, setPwModal] = useState(null);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    setLoading(true);
    portalApi.get(`/portal/profile/${targetId}`).then((r) => { setP(r.data); setWall(r.data.wall || []); setRecs(r.data.recordings || []); }).catch(() => {}).finally(() => setLoading(false));
  }, [targetId]);

  useEffect(() => {
    if (p?.is_self) portalApi.get("/portal/directory").then((r) => setDir(r.data)).catch(() => {});
    else setDir([]);
  }, [p?.is_self]);

  const postWall = async () => {
    if (!wallText.trim() && wallMedia.length === 0) return;
    setPosting(true);
    try {
      const { data } = await portalApi.post(`/portal/profile/${targetId}/wall`, { text: wallText, media: wallMedia });
      setWall((w) => [data, ...w]); setWallText(""); setWallMedia([]);
      toast.success("Posted to their page.");
    } catch (e) { toast.error(apiError(e)); } finally { setPosting(false); }
  };

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (!p.is_self) fd.append("member_id", targetId);
      const { data } = await portalApi.post("/portal/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((prev) => ({ ...prev, avatar: data.avatar }));
      if (p.is_self) await refreshUser();
      toast.success("Photo updated.");
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingAvatar(false); e.target.value = ""; }
  };

  const toggleRec = async (rec, field) => {
    try {
      const { data } = await portalApi.put(`/portal/recordings/${rec.id}`, { [field]: !rec[field] });
      setRecs((rs) => rs.map((r) => (r.id === rec.id ? data : r)));
      toast.success("Updated.");
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteRec = async (rec) => {
    try { await portalApi.delete(`/portal/recordings/${rec.id}`); setRecs((rs) => rs.filter((r) => r.id !== rec.id)); toast.success("Removed."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveName = async () => {
    setSavingName(true);
    try {
      const { data } = await portalApi.put(`/portal/members/${targetId}`, { name: editName.name, email: editName.email });
      setP((prev) => ({ ...prev, name: data.name }));
      setEditName(null);
      if (p.is_self) await refreshUser();
      toast.success("Saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingName(false); }
  };
  const changePassword = async () => {
    if (!pwModal.next || pwModal.next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (pwModal.next !== pwModal.confirm) return toast.error("The two passwords don't match.");
    setSavingPw(true);
    try {
      await portalApi.put("/portal/profile/password", { current_password: pwModal.current, new_password: pwModal.next });
      setPwModal(null);
      toast.success("Your password has been changed.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPw(false); }
  };

  if (loading) return <p className="text-[#8a7a6a] font-body" data-testid="portal-profile">Loading…</p>;
  if (!p) return <p className="text-[#8a7a6a] font-body" data-testid="portal-profile">Profile not found.</p>;

  const earned = p.badges.filter((b) => b.earned);
  const voice = wallMedia.filter((m) => m.type === "audio");
  const canEditAvatar = p.is_self || user?.role === "admin";

  return (
    <div data-testid="portal-profile" className="max-w-3xl">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">{p.cohort} · {p.track || "cohort"}</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar name={p.name} avatar={p.avatar} size={76} testid="profile-avatar" />
            {canEditAvatar && (
              <label data-testid="profile-avatar-upload" title="Change photo"
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] flex items-center justify-center cursor-pointer shadow-md transition-colors">
                {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />}
                <input type="file" accept="image/*" className="hidden" onChange={onAvatarFile} disabled={uploadingAvatar} data-testid="profile-avatar-input" />
              </label>
            )}
          </div>
          <h1 className="font-serif-display font-light text-4xl md:text-5xl flex items-center gap-3">
            {p.name}{p.is_self && <span className="text-[#A9724E] text-lg italic">you</span>}
            {p.is_self && (
              <button data-testid="profile-edit-name" onClick={() => setEditName({ name: p.name, email: user?.email || "" })}
                className="text-[#A9724E] hover:text-[#925C3B] transition-colors" title="Edit your name">
                <Pencil className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}
            {p.is_self && (
              <button data-testid="profile-change-password" onClick={() => setPwModal({ current: "", next: "", confirm: "" })}
                className="text-[#A9724E] hover:text-[#925C3B] transition-colors" title="Change your password">
                <Lock className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-[#A9724E]">
          <Droplet className="w-6 h-6" strokeWidth={1.5} />
          <span className="font-serif-display text-3xl text-[#2A2825]">{p.drops}</span>
          <span className="text-sm text-[#5E4B3C] font-body">drops</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <LiveButton scope="member" targetId={targetId} title={`${p.name.split(" ")[0]}'s room`} label={p.is_self ? "Open my live room" : `Drop into ${p.name.split(" ")[0]}'s room`} testid="profile-go-live" />
        {!p.is_self && (
          <button data-testid="profile-message" onClick={async () => { try { const { data } = await portalApi.post("/portal/conversations", { type: "dm", member_ids: [targetId] }); navigate(`/portal/messages?c=${data.id}`); } catch (e) { toast.error(apiError(e)); } }}
            className="inline-flex items-center gap-2 rounded-full border border-[#D8CEBE] hover:bg-white/60 text-[#5E4B3C] px-4 py-2 text-sm transition-colors">
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />Message
          </button>
        )}
      </div>

      {/* blessing card — how to encourage this member */}
      {p.about && <AboutCard about={p.about} isSelf={p.is_self} firstName={p.name.split(" ")[0]} />}

      {/* notification preferences (own profile only) */}
      {p.is_self && <NotifPrefs profile={p} />}

      {/* member directory (own profile only) */}
      {p.is_self && dir.length > 0 && (
        <section className="mb-12" data-testid="profile-directory">
          <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4 flex items-center gap-2"><Users className="w-3.5 h-3.5" strokeWidth={1.5} />Your home · tap a name to visit their page</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {dir.filter((m) => m.id !== p.id).map((m) => (
              <Link key={m.id} to={`/portal/profile/${m.id}`} data-testid={`directory-member-${m.id}`}
                className="flex items-center gap-3 bg-white/60 border border-[#E0D8C9] rounded-xl p-3 hover:border-[#A9724E] transition-colors">
                <Avatar name={m.name} avatar={m.avatar} size={36} testid={`directory-avatar-${m.id}`} />
                <span className="min-w-0"><span className="block font-serif-display text-sm text-[#2A2825] truncate">{m.name}</span><span className="text-[10px] text-[#8a7a6a] font-body">{m.drops} drops</span></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* badges */}
      <section className="mb-12" data-testid="profile-badges">
        <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">Badges · {earned.length} of {p.badges.length} earned</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {p.badges.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || Droplet;
            return (
              <button key={b.key} data-testid={`badge-${b.key}`} onClick={() => setBadgeDetail(b)}
                className={`rounded-xl border p-4 text-center transition-colors ${b.earned ? "bg-[#EAE3D9] border-[#D8CEBE] hover:border-[#A9724E]" : "bg-white/40 border-[#E0D8C9] opacity-45"}`}>
                <Icon className={`w-7 h-7 mx-auto mb-2 ${b.earned ? "text-[#A9724E]" : "text-[#b8ad9c]"}`} strokeWidth={1.5} />
                <p className="font-serif-display text-sm text-[#2A2825] leading-tight">{b.name}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* recorded sessions */}
      {(recs.length > 0 || p.is_self) && (
        <section className="mb-12" data-testid="profile-recordings">
          <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">{p.is_self ? "Your recorded sessions" : `${p.name.split(" ")[0]}'s recorded sessions`}</p>
          {recs.length === 0 ? (
            <p className="text-sm text-[#8a7a6a] font-body">No recordings yet. Start a call, tap Record, and it saves here.</p>
          ) : (
            <div className="space-y-4">
              {recs.map((r) => (
                <RecordingItem key={r.id} rec={r}
                  onToggle={(field) => toggleRec(r, field)} onDelete={() => deleteRec(r)}
                  onChange={(u) => setRecs((rs) => rs.map((x) => (x.id === u.id ? u : x)))} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* wall — post directly on this member's page */}
      <section className="mb-12" data-testid="profile-wall">
        <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">{p.is_self ? "Your page" : `Leave something for ${p.name.split(" ")[0]}`}</p>
        <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-4 mb-5">
          <textarea data-testid="wall-text" value={wallText} onChange={(e) => setWallText(e.target.value)} rows={2}
            placeholder={p.is_self ? "Write something on your own page…" : `A word of encouragement, a photo, a voice note for ${p.name.split(" ")[0]}…`}
            className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body text-[#2A2825] outline-none focus:border-[#A9724E] resize-none" />
          {voice.length > 0 && <div className="mt-2">{voice.map((m, i) => <VoicePlayer key={i} path={m.path} />)}</div>}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <MediaAttach testid="wall-media" value={wallMedia.filter((m) => m.type !== "audio")} onChange={(vis) => setWallMedia([...voice, ...vis])} scope="cohort" />
              <VoiceRecorder testid="wall-voice" onRecorded={(path) => setWallMedia((m) => [...m, { type: "audio", path }])} />
            </div>
            <button data-testid="wall-submit" onClick={postWall} disabled={posting || (!wallText.trim() && wallMedia.length === 0)}
              className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
              <Send className="w-4 h-4" strokeWidth={1.5} />{posting ? "Posting…" : "Post"}
            </button>
          </div>
        </div>
        {wall.length === 0 ? <p className="text-sm text-[#8a7a6a] font-body">No notes on this page yet.</p> : (
          <div className="space-y-4">
            {wall.map((w) => (
              <div key={w.id} data-testid={`wall-post-${w.id}`} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <Link to={`/portal/profile/${w.user_id}`} className="font-serif-display text-[#A9724E] hover:underline">{w.author_name}</Link>
                  {w.date && <span className="text-[10px] text-[#8a7a6a] font-body">{w.date}</span>}
                </div>
                {w.text && <p className="text-[#2A2825] font-body leading-relaxed whitespace-pre-wrap">{w.text}</p>}
                <MediaGrid media={w.media} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* shared reflections */}
      <section data-testid="profile-entries">
        <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">{p.is_self ? "Your reflections" : "Shared reflections"}</p>
        {p.entries.length === 0 ? (
          <p className="text-[#8a7a6a] font-body">Nothing shared yet.</p>
        ) : (
          <div className="space-y-4">
            {p.entries.map((e) => (
              <div key={e.id} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="tracking-overline text-[10px] uppercase text-[#A9724E]">{TYPE_LABEL[e.type] || e.type}</span>
                  {e.date && <span className="text-[10px] text-[#8a7a6a] font-body">{e.date}</span>}
                  {e.is_private && <span className="inline-flex items-center gap-1 text-[10px] tracking-overline uppercase text-[#b45c5c]"><Lock className="w-3 h-3" strokeWidth={1.5} />private</span>}
                </div>
                <p className="text-[#2A2825] font-body leading-relaxed whitespace-pre-wrap">{e.text}</p>
                <MediaGrid media={e.media} />
              </div>
            ))}
          </div>
        )}
      </section>

      {!p.is_self && (
        <Link to="/portal/community" className="inline-block mt-10 text-sm text-[#A9724E] font-body hover:underline">← Back to Community</Link>
      )}

      {badgeDetail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setBadgeDetail(null)}>
          <div data-testid="badge-detail-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {(() => { const Icon = BADGE_ICONS[badgeDetail.icon] || Droplet; return (
              <div className="w-14 h-14 rounded-full bg-[#EAE3D9] flex items-center justify-center mb-4">
                <Icon className={`w-7 h-7 ${badgeDetail.earned ? "text-[#A9724E]" : "text-[#b8ad9c]"}`} strokeWidth={1.5} />
              </div>
            ); })()}
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">{badgeDetail.earned ? "Earned" : "Not yet earned"}</p>
            <h3 className="font-serif-display text-2xl mb-1">{badgeDetail.name}</h3>
            <p className="text-sm text-[#8a7a6a] font-body mb-4">{badgeDetail.desc}</p>
            {badgeDetail.scripture && (
              <div className="bg-[#EAE3D9]/60 rounded-lg px-4 py-3 mb-4">
                <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1">{badgeDetail.scripture_ref}</p>
                <p className="font-serif-display text-[#2A2825] italic leading-relaxed">“{badgeDetail.scripture}”</p>
              </div>
            )}
            {badgeDetail.walking && <p className="text-[#2A2825] font-body leading-relaxed">{badgeDetail.walking}</p>}
            <button onClick={() => setBadgeDetail(null)} className="mt-6 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors">Close</button>
          </div>
        </div>
      )}

      {editName && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditName(null)}>
          <div data-testid="edit-name-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">Your details</p>
            <h3 className="font-serif-display text-2xl mb-5">Edit your name</h3>
            <label className="block text-sm text-[#5E4B3C] mb-1.5">Name</label>
            <input data-testid="edit-name-input" value={editName.name} onChange={(e) => setEditName({ ...editName, name: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <label className="block text-sm text-[#5E4B3C] mb-1.5">Email (for sign in)</label>
            <input data-testid="edit-email-input" type="email" value={editName.email} onChange={(e) => setEditName({ ...editName, email: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-5" />
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setEditName(null)} className="text-sm text-[#8a7a6a]">Cancel</button>
              <button data-testid="edit-name-save" onClick={saveName} disabled={savingName || !editName.name.trim()}
                className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">{savingName ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      {pwModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPwModal(null)}>
          <div data-testid="change-password-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">Security</p>
            <h3 className="font-serif-display text-2xl mb-5">Change your password</h3>
            <label className="block text-sm text-[#5E4B3C] mb-1.5">Current password</label>
            <input data-testid="pw-current" type="password" value={pwModal.current} onChange={(e) => setPwModal({ ...pwModal, current: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <label className="block text-sm text-[#5E4B3C] mb-1.5">New password</label>
            <input data-testid="pw-next" type="password" value={pwModal.next} onChange={(e) => setPwModal({ ...pwModal, next: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <label className="block text-sm text-[#5E4B3C] mb-1.5">Confirm new password</label>
            <input data-testid="pw-confirm" type="password" value={pwModal.confirm} onChange={(e) => setPwModal({ ...pwModal, confirm: e.target.value })}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-5" />
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setPwModal(null)} className="text-sm text-[#8a7a6a]">Cancel</button>
              <button data-testid="pw-save" onClick={changePassword} disabled={savingPw}
                className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">{savingPw ? "Saving…" : "Change password"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const fmtT = (s) => { if (s == null) return "—"; const m = Math.floor(s / 60), sec = Math.floor(s % 60); return `${m}:${String(sec).padStart(2, "0")}`; };

function RecordingItem({ rec, onToggle, onDelete, onChange }) {
  const videoRef = useRef(null);
  const stopAtRef = useRef(null);
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMark, setShowMark] = useState(false);

  const playRange = (s, e) => { const v = videoRef.current; if (!v) return; v.currentTime = s; stopAtRef.current = e; v.play(); };
  const onTimeUpdate = () => { const v = videoRef.current; if (v && stopAtRef.current != null && v.currentTime >= stopAtRef.current) { v.pause(); stopAtRef.current = null; } };
  const cur = () => Math.round((videoRef.current?.currentTime || 0) * 100) / 100;

  const saveHighlight = async () => {
    if (start == null || end == null || end <= start) { toast.error("Set a start, then an end after it."); return; }
    setSaving(true);
    try {
      const { data } = await portalApi.post(`/portal/recordings/${rec.id}/highlights`, { title: title || "Highlight", start, end });
      onChange(data); setTitle(""); setStart(null); setEnd(null); setShowMark(false); toast.success("Highlight saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const delHighlight = async (hid) => {
    try { const { data } = await portalApi.delete(`/portal/recordings/${rec.id}/highlights/${hid}`); onChange(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={`recording-${rec.id}`} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-4">
      <p className="font-serif-display text-lg text-[#2A2825] mb-2">{rec.title}</p>
      <video ref={videoRef} src={mediaUrl(rec.path)} controls onTimeUpdate={onTimeUpdate} className="w-full rounded-lg bg-black max-h-72" />

      {(rec.highlights || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`recording-highlights-${rec.id}`}>
          {rec.highlights.map((h) => (
            <span key={h.id} className="inline-flex items-center gap-2 rounded-full bg-[#EAE3D9] border border-[#D8CEBE] pl-3 pr-1.5 py-1 text-xs">
              <button data-testid={`play-highlight-${h.id}`} onClick={() => playRange(h.start, h.end)} className="inline-flex items-center gap-1.5 text-[#2A2825] hover:text-[#A9724E]">
                <Play className="w-3 h-3 fill-current" strokeWidth={0} />{h.title} <span className="text-[#8a7a6a]">{fmtT(h.start)}–{fmtT(h.end)}</span>
              </button>
              {rec.is_host && <button onClick={() => delHighlight(h.id)} className="text-[#b45c5c] hover:text-[#933]"><XIcon className="w-3 h-3" /></button>}
            </span>
          ))}
        </div>
      )}

      {rec.is_host && (
        <>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
            <label className="flex items-center gap-2 text-[#5E4B3C] cursor-pointer"><input data-testid={`rec-community-${rec.id}`} type="checkbox" checked={rec.publish_community} onChange={() => onToggle("publish_community")} />In Community</label>
            <label className="flex items-center gap-2 text-[#5E4B3C] cursor-pointer"><input data-testid={`rec-library-${rec.id}`} type="checkbox" checked={rec.publish_library} onChange={() => onToggle("publish_library")} />In Library</label>
            <button data-testid={`mark-highlight-${rec.id}`} onClick={() => setShowMark((s) => !s)} className="inline-flex items-center gap-1.5 text-[#A9724E] hover:text-[#925C3B]"><Scissors className="w-3.5 h-3.5" strokeWidth={1.5} />Mark a highlight</button>
            <button data-testid={`rec-delete-${rec.id}`} onClick={onDelete} className="ml-auto text-[#b45c5c] hover:underline">Delete</button>
          </div>
          {showMark && (
            <div className="mt-3 bg-[#F3E9DE] border border-[#E0D0BC] rounded-lg p-3" data-testid={`mark-panel-${rec.id}`}>
              <p className="text-xs text-[#8a7a6a] font-body mb-2">Play the video, then mark where the moment starts and ends.</p>
              <input data-testid={`highlight-title-${rec.id}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name this moment…"
                className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-1.5 text-sm font-body outline-none focus:border-[#A9724E] mb-2" />
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <button data-testid={`set-start-${rec.id}`} onClick={() => setStart(cur())} className="rounded-full border border-[#D8CEBE] bg-white px-3 py-1.5 hover:border-[#A9724E]">Set start · {fmtT(start)}</button>
                <button data-testid={`set-end-${rec.id}`} onClick={() => setEnd(cur())} className="rounded-full border border-[#D8CEBE] bg-white px-3 py-1.5 hover:border-[#A9724E]">Set end · {fmtT(end)}</button>
                <button data-testid={`save-highlight-${rec.id}`} onClick={saveHighlight} disabled={saving} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-4 py-1.5 disabled:opacity-50">{saving ? "Saving…" : "Save highlight"}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
